import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { Button, Dropdown, Fade, Icon, Input, SegmentedControl, Textarea } from '@column/column-ui-kit';
import { Tabs, TabsList, TabsTrigger, cn } from '@column/ui';

import { useBreadcrumbTitle } from '~/app/Layout/PageBreadcrumbs';
import { PageActions } from '~/app/Layout/PageFrameContext';
import { ROUTE } from '~/app/routes';
import { ApiKeyInput, PopoverBase, RenderFields, RestrictedBanner } from '~/components';
import { LogoLoading } from '~/elements';
import { FeatureFlag, useFeatureFlag } from '~/lib/flags';
import { useNavigate } from '~/lib/navigation';
import PermissionConfiguration, { Mode, PermissionConfigurationSection, useEntitiesAndAccounts } from '~/lib/roles';
import { APIKeyPermissions, ApiKeyRepository } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';

interface Params {
  id: string;
  URLType: string;
}

enum Section {
  Transfers = 'api-key-transfers-and-accounts',
  Additional = 'api-key-additional-permissions',
}

enum APIKeyPermissionPreset {
  ViewOnly = 'view-only',
  FullAccess = 'full-access',
  Custom = 'custom',
}

type APIKeyEnvironment = 'production' | 'sandbox';

interface APIKeyFormValues {
  apiKeyToken?: string;
  description: string;
  name: string;
  permissions: APIKeyPermissions;
  platformId: string;
  type: APIKeyEnvironment;
}

type APIKeySaveValues = Omit<APIKeyFormValues, 'apiKeyToken'>;

interface PermissionsPanelSnapshot {
  permissions: APIKeyPermissions;
  preset: APIKeyPermissionPreset;
}

const excludedAPIKeyPermissionKeys = ['apiKeys', 'approveTransfers'] as const;
const includedAPIKeyAdditionalPermissionKeys = ['counterparties', 'loans', 'reporting'] as const;
const apiKeyVisiblePlatformPermissionKeys = [
  'achCredits',
  'achDebits',
  'achReturns',
  'bankAccounts',
  'bookTransfers',
  'counterparties',
  'depositChecks',
  'entities',
  'issueChecks',
  'loans',
  'realtimeTransfers',
  'reporting',
  'stopPayments',
  'wireTransfers',
] as const satisfies readonly (keyof APIKeyPermissions['platformLevelPermissions'])[];
const apiKeyVisibleBankAccountLevelOverrideKeys = [
  'achCredits',
  'achDebits',
  'achReturns',
  'bookTransfers',
  'depositChecks',
  'issueChecks',
  'realtimeTransfers',
  'stopPayments',
  'thisBankAccount',
  'wireTransfers',
] as const;
const apiKeyVisibleEntityLevelOverrideKeys = [
  'achCredits',
  'achDebits',
  'achReturns',
  'bankAccounts',
  'bookTransfers',
  'depositChecks',
  'issueChecks',
  'realtimeTransfers',
  'stopPayments',
  'thisEntity',
  'wireTransfers',
] as const;

const apiKeyPermissionPresetOptions = [
  {
    icon: <Icon.CircleCheck className="[--icon-color:var(--color-seafoam-600)]!" />,
    label: 'Full Access',
    value: APIKeyPermissionPreset.FullAccess,
  },
  {
    icon: <Icon.Eye className="[--icon-color:var(--color-primary)]!" />,
    label: 'View Only',
    value: APIKeyPermissionPreset.ViewOnly,
  },
  {
    icon: <Icon.Filters className="[--icon-color:var(--color-yellow-600)]!" />,
    label: 'Custom',
    value: APIKeyPermissionPreset.Custom,
  },
];
const apiKeySections = [
  { label: 'Transfers and Accounts', value: Section.Transfers },
  { label: 'Additional Permissions', value: Section.Additional },
];

const createAPIKeyPlatformLevelPermissions = (
  permissionLevel: 'read' | 'write',
  platformLevelPermissions?: APIKeyPermissions['platformLevelPermissions']
): APIKeyPermissions['platformLevelPermissions'] =>
  ({
    ...Object.fromEntries(
      apiKeyVisiblePlatformPermissionKeys.map((permission) => [
        permission,
        platformLevelPermissions?.[permission] ?? permissionLevel,
      ])
    ),
    reporting: platformLevelPermissions?.reporting ?? 'read',
  }) as unknown as APIKeyPermissions['platformLevelPermissions'];

const createDefaultAPIKeyPermissions = (): APIKeyPermissions => ({
  bankAccountLevelOverrides: [],
  entityLevelOverrides: [],
  platformLevelPermissions: createAPIKeyPlatformLevelPermissions('write'),
});

const createPresetAPIKeyPermissions = (permissionLevel: 'read' | 'write', platformId?: string): APIKeyFormValues => ({
  ...defaultFormValues(platformId),
  permissions: {
    bankAccountLevelOverrides: [],
    entityLevelOverrides: [],
    platformLevelPermissions: createAPIKeyPlatformLevelPermissions(permissionLevel),
  },
});

const defaultFormValues = (platformId?: string): APIKeyFormValues => ({
  apiKeyToken: '',
  description: '',
  name: '',
  permissions: createDefaultAPIKeyPermissions(),
  platformId: platformId ?? '',
  type: 'sandbox',
});

const getAPIKeyPermissionPreset = (
  platformLevelPermissions?: APIKeyPermissions['platformLevelPermissions']
): APIKeyPermissionPreset => {
  if (
    platformLevelPermissions &&
    apiKeyVisiblePlatformPermissionKeys.every((permission) => platformLevelPermissions[permission] === 'read')
  ) {
    return APIKeyPermissionPreset.ViewOnly;
  }
  if (
    platformLevelPermissions &&
    apiKeyVisiblePlatformPermissionKeys.every((permission) =>
      permission === 'reporting'
        ? platformLevelPermissions[permission] === 'read'
        : platformLevelPermissions[permission] === 'write'
    )
  ) {
    return APIKeyPermissionPreset.FullAccess;
  }
  return APIKeyPermissionPreset.Custom;
};

const getAPIKeyPermissionPresetFromPermissions = (permissions: APIKeyPermissions): APIKeyPermissionPreset => {
  if ((permissions.bankAccountLevelOverrides?.length ?? 0) > 0 || (permissions.entityLevelOverrides?.length ?? 0) > 0) {
    return APIKeyPermissionPreset.Custom;
  }

  return getAPIKeyPermissionPreset(permissions.platformLevelPermissions);
};

const trimOverrides = <T extends object>(
  overrides: T[] | undefined,
  idKey: keyof T,
  visibleKeys?: readonly string[]
): T[] =>
  (overrides ?? [])
    .map(
      (override) =>
        Object.fromEntries(
          Object.entries(override as Record<string, unknown>).filter(
            ([key, value]) => key === String(idKey) || (value !== 'default' && (visibleKeys?.includes(key) ?? true))
          )
        ) as T
    )
    .filter((override) => Object.keys(override).length > 1);

const normalizeAPIKeyPermissions = (permissions?: APIKeyPermissions | null): APIKeyPermissions => ({
  bankAccountLevelOverrides: trimOverrides(
    permissions?.bankAccountLevelOverrides,
    'bankAccountId',
    apiKeyVisibleBankAccountLevelOverrideKeys
  ),
  entityLevelOverrides: trimOverrides(
    permissions?.entityLevelOverrides,
    'entityId',
    apiKeyVisibleEntityLevelOverrideKeys
  ),
  platformLevelPermissions: createAPIKeyPlatformLevelPermissions('write', permissions?.platformLevelPermissions),
});

const sanitizeAPIKeyPermissions = (permissions: APIKeyPermissions): APIKeyPermissions => ({
  bankAccountLevelOverrides: trimOverrides(
    permissions.bankAccountLevelOverrides,
    'bankAccountId',
    apiKeyVisibleBankAccountLevelOverrideKeys
  ),
  entityLevelOverrides: trimOverrides(
    permissions.entityLevelOverrides,
    'entityId',
    apiKeyVisibleEntityLevelOverrideKeys
  ),
  platformLevelPermissions: createAPIKeyPlatformLevelPermissions('write', permissions.platformLevelPermissions),
});

const getPermissionSavePayload = (
  preset: APIKeyPermissionPreset,
  permissions: APIKeyPermissions
): APIKeyPermissions | null =>
  preset === APIKeyPermissionPreset.FullAccess ? null : sanitizeAPIKeyPermissions(permissions);

const copyAPIKeyPermissions = (permissions: APIKeyPermissions): APIKeyPermissions => ({
  bankAccountLevelOverrides: (permissions.bankAccountLevelOverrides ?? []).map((override) => ({ ...override })),
  entityLevelOverrides: (permissions.entityLevelOverrides ?? []).map((override) => ({ ...override })),
  platformLevelPermissions: { ...permissions.platformLevelPermissions },
});

const getAPIKeySaveValues = (data: APIKeyFormValues): APIKeySaveValues => ({
  description: data.description,
  name: data.name,
  permissions: data.permissions,
  platformId: data.platformId,
  type: data.type,
});

const serializeAPIKeySaveValues = (values: APIKeySaveValues): string => JSON.stringify(values);

export const PageApiKeysEdit: React.FC = () => {
  const { currentUser, currentPlatform, currentPermission } = useSessionStore();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const { id, URLType } = useParams<keyof Params>() as Params;
  const apiKeyPermissionsEnabled = useFeatureFlag(FeatureFlag.EnableAPIKeyPermissions);
  const [apiKeyName, setApiKeyName] = useState<string>('');
  const [isAPIKeyLoading, setIsAPIKeyLoading] = useState<boolean>(!!id);
  const [visibleSection, setVisibleSection] = useState<Section>(Section.Transfers);
  const [isPermissionsPanelOpen, setIsPermissionsPanelOpen] = useState<boolean>(false);
  const [savedAPIKeySaveValues, setSavedAPIKeySaveValues] = useState<APIKeySaveValues | null>(null);
  const [selectedPermissionPreset, setSelectedPermissionPreset] = useState<APIKeyPermissionPreset>(
    APIKeyPermissionPreset.FullAccess
  );

  const formMethods = useForm<APIKeyFormValues>({
    defaultValues: defaultFormValues(currentPlatform?.id ?? currentUser?.defaultPlatformId),
  });
  const { control, getValues, handleSubmit, reset, setValue } = formMethods;

  const permissionsPanelSnapshot = useRef<PermissionsPanelSnapshot | null>(null);

  const formValues = useWatch({ control }) as APIKeyFormValues;
  const selectedType = useWatch({ control, name: 'type' });
  const bankAccountLevelOverrides = useWatch({ control, name: 'permissions.bankAccountLevelOverrides' });
  const entityLevelOverrides = useWatch({ control, name: 'permissions.entityLevelOverrides' });

  const selectedMode = selectedType === 'production' ? Mode.Live : Mode.Sandbox;
  const platformId = currentPlatform?.id ?? currentUser?.defaultPlatformId ?? '';

  const existingBankAccountIDs = useMemo(
    () =>
      (bankAccountLevelOverrides ?? [])
        .map((bankAccount) => bankAccount.bankAccountId)
        .filter((bankAccountId): bankAccountId is string => typeof bankAccountId === 'string'),
    [bankAccountLevelOverrides]
  );
  const existingEntityIDs = useMemo(
    () =>
      (entityLevelOverrides ?? [])
        .map((entity) => entity.entityId)
        .filter((entityId): entityId is string => typeof entityId === 'string'),
    [entityLevelOverrides]
  );

  const sandboxEntitiesAndAccounts = useEntitiesAndAccounts({
    bankAccountIDs: existingBankAccountIDs,
    enabled: apiKeyPermissionsEnabled,
    entityIds: existingEntityIDs,
    mode: Mode.Sandbox,
  });
  const liveEntitiesAndAccounts = useEntitiesAndAccounts({
    bankAccountIDs: existingBankAccountIDs,
    enabled: apiKeyPermissionsEnabled,
    entityIds: existingEntityIDs,
    mode: Mode.Live,
  });

  useEffect(() => {
    if (!apiKeyPermissionsEnabled) {
      setIsPermissionsPanelOpen(false);
      permissionsPanelSnapshot.current = null;
    }
  }, [apiKeyPermissionsEnabled]);

  const openPermissionsPanel = useCallback(() => {
    permissionsPanelSnapshot.current = {
      permissions: copyAPIKeyPermissions({
        bankAccountLevelOverrides: getValues('permissions.bankAccountLevelOverrides') ?? [],
        entityLevelOverrides: getValues('permissions.entityLevelOverrides') ?? [],
        platformLevelPermissions:
          getValues('permissions.platformLevelPermissions') ??
          createDefaultAPIKeyPermissions().platformLevelPermissions,
      }),
      preset: selectedPermissionPreset,
    };
    setIsPermissionsPanelOpen(true);
  }, [getValues, selectedPermissionPreset]);

  const closePermissionsPanel = useCallback(() => {
    setIsPermissionsPanelOpen(false);
    permissionsPanelSnapshot.current = null;
  }, []);

  const submitPermissionsPanel = useCallback(() => {
    setSelectedPermissionPreset(
      getAPIKeyPermissionPresetFromPermissions(sanitizeAPIKeyPermissions(getValues('permissions')))
    );
    closePermissionsPanel();
  }, [closePermissionsPanel, getValues]);

  const cancelPermissionsPanel = useCallback(() => {
    const snapshot = permissionsPanelSnapshot.current;

    if (snapshot) {
      setSelectedPermissionPreset(snapshot.preset);
      setValue('permissions.bankAccountLevelOverrides', snapshot.permissions.bankAccountLevelOverrides, {
        shouldDirty: true,
      });
      setValue('permissions.entityLevelOverrides', snapshot.permissions.entityLevelOverrides, { shouldDirty: true });
      setValue('permissions.platformLevelPermissions', snapshot.permissions.platformLevelPermissions, {
        shouldDirty: true,
      });
    }

    closePermissionsPanel();
  }, [closePermissionsPanel, setValue]);

  const handlePermissionPresetChange = useCallback(
    (preset: APIKeyPermissionPreset) => {
      setSelectedPermissionPreset(preset);

      if (preset === APIKeyPermissionPreset.Custom) {
        setValue(
          'permissions.platformLevelPermissions',
          getValues('permissions.platformLevelPermissions') ??
            createDefaultAPIKeyPermissions().platformLevelPermissions,
          { shouldDirty: true, shouldTouch: true }
        );
        openPermissionsPanel();
        return;
      }

      const permissionLevel = preset === APIKeyPermissionPreset.ViewOnly ? 'read' : 'write';
      const presetPermissions = createPresetAPIKeyPermissions(permissionLevel, platformId);

      setIsPermissionsPanelOpen(false);
      setValue('permissions.bankAccountLevelOverrides', [], { shouldDirty: true, shouldTouch: true });
      setValue('permissions.entityLevelOverrides', [], { shouldDirty: true, shouldTouch: true });
      setValue('permissions.platformLevelPermissions', presetPermissions.permissions.platformLevelPermissions, {
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    [getValues, openPermissionsPanel, platformId, setValue]
  );

  const handleEnvironmentChange = useCallback(
    (environment: APIKeyEnvironment) => {
      setValue('type', environment, { shouldDirty: true, shouldTouch: true });
      setValue('permissions.bankAccountLevelOverrides', [], { shouldDirty: true, shouldTouch: true });
      setValue('permissions.entityLevelOverrides', [], { shouldDirty: true, shouldTouch: true });
    },
    [setValue]
  );

  useBreadcrumbTitle(id ? apiKeyName : 'Create');

  const fetchData = useCallback(() => {
    if (!id || !currentUser || !platformId) {
      return;
    }

    setIsAPIKeyLoading(true);
    ApiKeyRepository.get(platformId, id, URLType === 'Sandbox')
      .then((response) => {
        const responsePermissions = response.permissions
          ? normalizeAPIKeyPermissions(response.permissions)
          : createDefaultAPIKeyPermissions();
        const nextFormValues: APIKeyFormValues = {
          ...defaultFormValues(platformId),
          apiKeyToken: response.apiKeyToken,
          description: response.description,
          name: response.name,
          permissions: responsePermissions,
          type: URLType === 'Production' ? 'production' : 'sandbox',
        };
        const nextPermissionPreset = response.permissions
          ? getAPIKeyPermissionPresetFromPermissions(responsePermissions)
          : APIKeyPermissionPreset.FullAccess;

        setApiKeyName(response.name);
        setSelectedPermissionPreset(nextPermissionPreset);
        setSavedAPIKeySaveValues(getAPIKeySaveValues(nextFormValues));
        reset(nextFormValues);
      })
      .catch((error: Error) => {
        navigate(ROUTE.PLATFORM_API_KEYS);
        addDangerNotification({
          content: error.message,
        });
      })
      .finally(() => {
        setIsAPIKeyLoading(false);
      });
  }, [URLType, addDangerNotification, currentUser, id, navigate, platformId, reset]);

  useEffect(() => {
    if (id) {
      fetchData();
      return;
    }
    setIsAPIKeyLoading(false);
    setSelectedPermissionPreset(APIKeyPermissionPreset.FullAccess);
    setSavedAPIKeySaveValues(null);
    reset(defaultFormValues(platformId));
  }, [fetchData, id, platformId, reset]);

  const hasUnsavedChanges = useMemo(() => {
    if (!id) {
      return true;
    }

    if (!savedAPIKeySaveValues) {
      return false;
    }

    return (
      serializeAPIKeySaveValues(getAPIKeySaveValues(formValues)) !== serializeAPIKeySaveValues(savedAPIKeySaveValues)
    );
  }, [formValues, id, savedAPIKeySaveValues]);

  const handleSave = (data: APIKeyFormValues) => {
    if (!currentUser) {
      return;
    }

    const saveValues = getAPIKeySaveValues(data);
    const permissionSavePayload = getPermissionSavePayload(selectedPermissionPreset, saveValues.permissions);
    const permissions = apiKeyPermissionsEnabled ? permissionSavePayload : undefined;
    const sandbox = id ? URLType === 'Sandbox' : saveValues.type === 'sandbox';
    const previousSavedAPIKeySaveValues = savedAPIKeySaveValues;

    if (id) {
      setSavedAPIKeySaveValues(saveValues);
      ApiKeyRepository.update(saveValues.platformId, id, saveValues.name, saveValues.description, sandbox, permissions)
        .then(() => {
          setApiKeyName(saveValues.name);
          reset(data);
          addSuccessNotification({
            content: 'API key updated',
          });
        })
        .catch((error) => {
          setSavedAPIKeySaveValues(previousSavedAPIKeySaveValues);
          addDangerNotification({
            content: error.message,
          });
        });
      return;
    }

    ApiKeyRepository.create(saveValues.platformId, saveValues.name, saveValues.description, sandbox, permissions)
      .then((response) => {
        navigate(
          `${ROUTE.PLATFORM_API_KEYS}/edit/${saveValues.type === 'sandbox' ? 'Sandbox' : 'Production'}/${response.id}`
        );
        addSuccessNotification({
          content: 'API key created',
        });
      })
      .catch((error) => {
        addDangerNotification({
          content: error.message,
        });
      });
  };
  const handleCancel = useCallback(() => {
    if (id) {
      navigate(ROUTE.PLATFORM_API_KEYS);
      return;
    }
    navigate(-1);
  }, [id, navigate]);

  const isEditLoading = !!id && isAPIKeyLoading;

  const backAction = (
    <Button onClick={handleCancel} variant="secondary" size="small">
      Cancel
    </Button>
  );
  const saveAction = (
    <Button
      onClick={handleSubmit(handleSave)}
      isDisabled={currentPermission?.apiKeys !== 'write'}
      size="small"
      icon={!id && <Icon.Plus />}
    >
      {id ? 'Save API Key' : 'Create API Key'}
    </Button>
  );

  const actions =
    id && !hasUnsavedChanges ? (
      <PageActions key="api-key-back-action">{backAction}</PageActions>
    ) : (
      <PageActions key="api-key-save-actions">
        {backAction}
        {saveAction}
      </PageActions>
    );

  return (
    <>
      <Fade show={isEditLoading}>
        <LogoLoading className="!top-20" />
      </Fade>
      <Fade show={!isEditLoading}>
        {currentPermission?.apiKeys !== 'write' && (
          <Inner pb={0}>
            <RestrictedBanner />
          </Inner>
        )}
        {actions}
        <FormProvider {...formMethods}>
          <form autoComplete="off" onSubmit={handleSubmit(handleSave)}>
            {RenderFields<APIKeyFormValues>({
              sections: [
                {
                  fields: [
                    {
                      id: 'name',
                      label: 'Name',
                      rules: { required: true },
                      children: ({ value, onChange, onBlur, ref }, { isTouched, error }) => {
                        return (
                          <Input
                            value={String(value)}
                            onChange={onChange}
                            onBlur={onBlur}
                            ref={ref}
                            placeholder="Name"
                            hasError={isTouched && !!error}
                          />
                        );
                      },
                    },
                    {
                      id: 'description',
                      label: 'Description',
                      children: ({ value, onChange, onBlur, ref }, { isTouched, error }) => {
                        return (
                          <Textarea
                            value={value !== undefined ? String(value) : ''}
                            onChange={onChange}
                            onBlur={onBlur}
                            ref={ref}
                            placeholder="Description"
                            hasError={isTouched && !!error}
                          />
                        );
                      },
                    },
                    {
                      id: 'type',
                      label: 'Environment',
                      rules: { required: true },
                      children: ({ value, onChange }) => {
                        return (
                          <SegmentedControl
                            options={[
                              ...(currentPlatform?.isLiveEnabled
                                ? [
                                    {
                                      label: 'Live',
                                      value: 'production',
                                    },
                                  ]
                                : []),
                              {
                                label: 'Sandbox',
                                value: 'sandbox',
                              },
                            ]}
                            active={id ? URLType.toLowerCase() : value}
                            onOptionChange={(environment) => {
                              onChange(environment);
                              handleEnvironmentChange(environment as APIKeyEnvironment);
                            }}
                            isDisabled={!!id}
                          />
                        );
                      },
                    },
                    ...(apiKeyPermissionsEnabled
                      ? [
                          {
                            id: 'permissions.platformLevelPermissions' as const,
                            label: 'Permissions',
                            children: () => (
                              <>
                                <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2">
                                  <Dropdown
                                    active={selectedPermissionPreset}
                                    options={apiKeyPermissionPresetOptions}
                                    onChange={(preset) =>
                                      handlePermissionPresetChange(preset as APIKeyPermissionPreset)
                                    }
                                    fullWidth
                                  />
                                  {selectedPermissionPreset === APIKeyPermissionPreset.Custom && (
                                    <Button
                                      className="h-10"
                                      type="button"
                                      variant="secondary"
                                      size="small"
                                      icon={<Icon.Edit />}
                                      onClick={openPermissionsPanel}
                                    >
                                      Edit
                                    </Button>
                                  )}
                                </div>
                              </>
                            ),
                          },
                        ]
                      : []),
                  ],
                },
                {
                  fields: [
                    {
                      id: 'apiKeyToken',
                      label: 'API Key',
                      children: ({ value }) => {
                        return (
                          <ApiKeyInput
                            value={value === undefined ? '' : String(value)}
                            placeholder="Key Will be Generated"
                          />
                        );
                      },
                    },
                  ],
                },
              ],
            })}
            {apiKeyPermissionsEnabled && (
              <PopoverBase
                show={isPermissionsPanelOpen}
                usePortal
                withoutBackdrop
                className={cn(
                  '![--animation-enter-x:24px] ![--animation-exit-x:24px] ![--animation-enter-y:0px] ![--animation-exit-y:0px]',
                  '[--popover-left:-24px] [--popover-padding:0] [--popover-position:fixed] [--popover-right:0] [--popover-top:0px] [--popover-width:min(100vw,700px)]',
                  'fixed! top-0! right-0! bottom-0! left-[-24px]! z-[14]! flex! h-dvh! w-[calc(100vw+24px)]! justify-end! overflow-hidden! bg-[rgba(18,18,22,0.56)]',
                  '[&>div]:h-dvh! [&>div]:w-[min(100vw,700px)]! [&>div]:rounded-none! [&>div]:flex [&>div]:flex-col',
                  '[&>div]:shadow-[1px_0_0_var(--color-border),-1px_0_0_var(--color-border),0_4px_16px_rgba(38,38,44,0.03)]!'
                )}
              >
                <div className="flex items-center gap-4 border-b border-gray-200 bg-background px-5 py-4">
                  <h4 className="m-0 flex-auto text-base font-medium leading-5 text-foreground">Permissions</h4>
                  <Button
                    type="button"
                    variant="subtle"
                    size="small"
                    icon={<Icon.Cross />}
                    onClick={cancelPermissionsPanel}
                  />
                </div>
                <Tabs
                  value={visibleSection}
                  onValueChange={(section) => setVisibleSection(section as Section)}
                  className="px-3 py-0.5 shadow-[0_1px_0_0_color-mix(in_oklch,var(--color-gray-950)_8%,transparent)] bg-gray-25 z-10"
                >
                  <TabsList variant="ghost" className="gap-0.5">
                    {apiKeySections.map((section) => (
                      <TabsTrigger key={section.value} value={section.value}>
                        {section.label}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
                <div className="flex-auto overflow-y-auto [--page-margin:24px]">
                  <PermissionConfiguration
                    visibleSection={
                      visibleSection === Section.Transfers
                        ? PermissionConfigurationSection.Transfers
                        : PermissionConfigurationSection.Additional
                    }
                    excludeKeys={[...excludedAPIKeyPermissionKeys]}
                    fieldPathPrefix="permissions"
                    forcedMode={selectedMode}
                    includeAdditionalPermissionKeys={[...includedAPIKeyAdditionalPermissionKeys]}
                    onOffEnabledPermissionLevel="read"
                    sandboxEntitiesAndAccounts={sandboxEntitiesAndAccounts}
                    liveEntitiesAndAccounts={liveEntitiesAndAccounts}
                  />
                </div>
                <div className="box-border flex justify-between gap-5 border-t border-gray-200 bg-gray-25 px-6 py-5">
                  <Button type="button" variant="secondary" onClick={cancelPermissionsPanel}>
                    Cancel
                  </Button>
                  <Button type="button" onClick={submitPermissionsPanel}>
                    Done
                  </Button>
                </div>
              </PopoverBase>
            )}
          </form>
        </FormProvider>
      </Fade>
    </>
  );
};
